import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { ReactNode } from 'react';
import { Button, Card, Wordmark } from '../../ui';
import { getMyClientRow } from '../../shared/queries';
import { supabase } from '../../shared/supabase/client';
import { useQuery } from '@tanstack/react-query';
import {
  OPTIONAL_CONSENTS,
  REQUIRED_CONSENTS,
  buildConsentRecord,
  consentName,
  consentText,
} from '../../shared/consent';
import type { ConsentId, ConsentParties, ConsentRecord } from '../../shared/consent';
import { resolveLanguage } from '../../shared/language';
import type { Language } from '../../shared/language';
import { text } from '../../shared/text';
import type { MessageId, TextParams } from '../../shared/text';
import { useInstance } from '../instance/InstanceProvider';
import { ClientGuard } from './ClientGuard';
import { ClientLoading } from './ClientPage';
import { decide, mayEnter, useChoices } from './consentChoices';

/**
 * The way in: what this is, what it keeps, who sees it, and what a client has to
 * agree to before they are shown anything.
 *
 * ## It was designed last, deliberately
 *
 * A dependency-led plan puts this screen first because it is first
 * chronologically. `client-me` D4 argues the opposite and this is the result: a
 * consent screen is the one screen nobody wants to read, and it should be
 * written by people who can already say in plain words what the product does.
 * After three slices we can. Everything on it is a sentence about something that
 * exists.
 *
 * ## The two required ticks, and the collision behind the second one
 *
 * `client-me`'s own spec asks for **one** required acknowledgement and says
 * "continuing requires only the acknowledgement". `legal-review-at-the-gate`'s
 * carve-out — the short list of findings that must be built right the first time
 * because no later edit repairs them — requires a **second**: "a client SHALL
 * NOT reach any content without confirming" they are 18 or over, with neither a
 * date of birth nor an age stored.
 *
 * Both are built: the acknowledgement and the age confirmation are required, and
 * the three optional consents are not. That is a deliberate resolution in favour
 * of the carve-out, because the carve-out is the one of the two that cannot be
 * repaired later — and it is recorded here rather than absorbed silently,
 * because it is the owner's to confirm.
 *
 * Neither required tick is ticked by default, and the refusal names the one that
 * is missing rather than saying that something is.
 *
 * ## The carve-out's other half: what is recorded
 *
 * Every tick and every empty box produces a `ConsentRecord` carrying the
 * **verbatim sentence the client read**, with the coach's name and role already
 * resolved, the language, the version of the wording, which consent it was, the
 * surface, and the time. `src/shared/consent.ts` explains why each of those
 * fields cannot be reconstructed afterwards. Nothing is written to a database:
 * this change adds no table, and the records are kept on the device by
 * `./consentChoices.ts`, which is append-only and says out loud that it is not
 * the legal record.
 *
 * ## Language
 *
 * The product is Castilian only, decided by the owner on 2026-09-14, and there
 * is deliberately **no language switcher**: `SUPPORTED_LANGUAGES` has one entry.
 * The mechanism the spec asks for is built anyway and is the part that matters —
 * the language is resolved **before** a single statement is rendered, every
 * statement is rendered in it, and the form is keyed on it, so the day a second
 * language exists, changing it re-renders everything and clears every tick
 * already made. That is a property of this component, not of a control it does
 * not have, and its test forces a second language to prove it.
 */
export function StartPage() {
  return (
    <ClientGuard beforeAck>
      <Start />
    </ClientGuard>
  );
}

function Start() {
  const instance = useInstance();
  const navigate = useNavigate();
  const choices = useChoices();
  const me = useQuery({ queryKey: ['my-client-row'], queryFn: () => getMyClientRow(supabase) });

  // The language, settled before any wording renders. Everything below is
  // rendered in it and the form below is keyed on it.
  const language = resolveLanguage();

  if (me.isPending) return <ClientLoading />;

  // Already in. Somebody who has acknowledged and comes back to this address is
  // sent on rather than asked twice.
  if (mayEnter(me.data, choices)) {
    void navigate(`/c/${instance.slug}/today`, { replace: true });
    return <ClientLoading />;
  }

  return (
    <Acknowledgement
      key={language}
      language={language}
      parties={{ coach: instance.coachDisplayName, role: instance.roleLine }}
      slug={instance.slug}
      instanceName={instance.displayName}
      wordmarkText={instance.wordmarkText}
      logoUrl={instance.logoUrl}
      onDone={(records) => {
        decide(records);
        void navigate(`/c/${instance.slug}/today`, { replace: true });
      }}
    />
  );
}

/**
 * The form itself, with the language as a prop.
 *
 * Exported so its test can render it twice in two languages and assert that the
 * second render carries no tick from the first — the requirement that outlives
 * the absence of a switcher.
 */
export function Acknowledgement({
  language,
  parties,
  instanceName,
  wordmarkText,
  logoUrl,
  onDone,
}: {
  language: Language;
  parties: ConsentParties;
  slug: string;
  instanceName: string;
  wordmarkText: string | null;
  logoUrl: string | null;
  onDone: (records: ConsentRecord[]) => void;
}) {
  const [ticked, setTicked] = useState<Partial<Record<ConsentId, boolean>>>({});
  const [missing, setMissing] = useState<ConsentId | null>(null);

  /** Every word on this screen, in the language settled above and no other. */
  const t = (id: MessageId, params?: TextParams) => text(id, params, language);

  function toggle(id: ConsentId, on: boolean) {
    setTicked((current) => ({ ...current, [id]: on }));
    if (on && missing === id) setMissing(null);
  }

  function submit() {
    // The first required tick that is missing, named. Not "faltan campos".
    const absent = REQUIRED_CONSENTS.find((id) => ticked[id] !== true);
    if (absent !== undefined) {
      setMissing(absent);
      return;
    }

    const at = new Date().toISOString();
    const records = [...REQUIRED_CONSENTS, ...OPTIONAL_CONSENTS].map((consent) =>
      buildConsentRecord({
        consent,
        decision: ticked[consent] === true ? 'given' : 'declined',
        parties,
        surface: 'first-run',
        language,
        at,
      }),
    );
    onDone(records);
  }

  return (
    <main className="bg-surface min-h-dvh">
      <div className="mx-auto w-full max-w-2xl px-6 pt-6 pb-16">
        <Wordmark name={instanceName} text={wordmarkText} logoUrl={logoUrl} size="sm" />

        <h1 className="text-ink font-display mt-6 text-3xl leading-tight">
          {t('client.start.title')}
        </h1>
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">
          {t('client.start.intro', { coach: parties.coach })}
        </p>

        <Card className="mt-5">
          <Section title={t('client.start.storedTitle')}>
            {t('client.start.storedBody', { coach: parties.coach })}
          </Section>
          <Section title={t('client.start.whoTitle')}>
            {t('client.start.whoBody', { coach: parties.coach })}
          </Section>
          <Section title={t('client.start.rightsTitle')}>{t('client.start.rightsBody')}</Section>
        </Card>

        <h2 className="text-ink font-display mt-8 text-xl">{t('client.start.requiredTitle')}</h2>
        <div className="mt-2">
          {REQUIRED_CONSENTS.map((id) => (
            <Tick
              key={id}
              id={id}
              label={consentText(id, parties, language)}
              checked={ticked[id] === true}
              onChange={(on) => {
                toggle(id, on);
              }}
            />
          ))}
        </div>

        <h2 className="text-ink font-display mt-8 text-xl">{t('client.start.optionalTitle')}</h2>
        <p className="text-ink-soft mt-1 text-sm leading-relaxed">
          {t('client.start.optionalIntro')}
        </p>
        <div className="mt-2">
          {OPTIONAL_CONSENTS.map((id) => (
            <Tick
              key={id}
              id={id}
              label={consentText(id, parties, language)}
              checked={ticked[id] === true}
              onChange={(on) => {
                toggle(id, on);
              }}
            />
          ))}
        </div>

        {missing === null ? null : (
          <p role="alert" className="text-danger mt-4 text-sm">
            {t('client.start.missingAck', { name: consentName(missing, language) })}
          </p>
        )}

        <div className="mt-6">
          <Button variant="accent" size="lg" onClick={submit}>
            {t('client.start.continue')}
          </Button>
        </div>

        <p className="text-ink-muted mt-4 text-xs leading-relaxed">
          {t('client.start.onThisDevice')}
        </p>
      </div>
    </main>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-4 first:mt-0">
      <h2 className="text-ink text-sm font-semibold">{title}</h2>
      <p className="text-ink-soft mt-1 text-sm leading-relaxed">{children}</p>
    </div>
  );
}

/**
 * One box and the sentence it is about.
 *
 * The whole sentence is the label, so the touch target is the sentence and not a
 * 16 px square, and so that what a screen reader announces is exactly the text
 * the record will keep. Unticked by default, always: `checked` is driven from
 * state that starts empty, and there is no `defaultChecked` anywhere in this
 * file.
 */
function Tick({
  id,
  label,
  checked,
  onChange,
}: {
  id: ConsentId;
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label
      htmlFor={`consent-${id}`}
      className="border-border flex cursor-pointer items-start gap-3 border-b py-3 last:border-b-0"
    >
      <input
        id={`consent-${id}`}
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
        className="accent-primary mt-0.5 size-5 shrink-0"
      />
      <span className="text-ink text-sm leading-relaxed">{label}</span>
    </label>
  );
}
